/*
 * Copyright (C) 2025 to Present Applause App Quality, Inc. All rights reserved.
 */

import Quill from 'quill';
import { NgfFormControl } from '@ng-falcon/ngf-form/+model/ngf-form-control';
import { TextEditorTemplate } from '@ng-falcon/+legacy/text-editor/text-editor-templates/+model/text-editor-template.model';
import Toolbar from 'quill/modules/toolbar';
import { DialogService } from '@ng-falcon/dialog/dialog.service';
import { TextEditorTemplateSelectDialogComponent } from '@ng-falcon/+legacy/text-editor/text-editor-templates/+dialog/text-editor-template-select-dialog.component';
import { TextEditorSelectionEvent } from '@ng-falcon/+legacy/text-editor/text-editor-templates/+model/text-editor-selection-event.model';
import { tap } from 'rxjs';

const Module = Quill.import('core/module');

export interface NgfRichTextTemplateExtensionOptions {
    templates: readonly TextEditorTemplate[] | (() => readonly TextEditorTemplate[]);
    control: NgfFormControl<string>;
    dialogService: DialogService;
}

/**
 * Rich Text Template Extension for NgfRichTextEditor
 * 
 * This extension provides template functionality for the rich text editor,
 * allowing users to insert predefined templates into their content.
 * 
 * Features:
 * - Template insertion via toolbar button
 * - Support for static template arrays or dynamic template functions
 * - Integration with NgfFormControl for value management
 * - Custom template selection dialog
 */
export class NgfRichTextTemplateExtension extends Module<NgfRichTextTemplateExtensionOptions> {

    quill: Quill;
    private templates: readonly TextEditorTemplate[] | (() => readonly TextEditorTemplate[]);
    private dialogService: DialogService;

    static DEFAULTS: NgfRichTextTemplateExtensionOptions = {
        templates: [],
        control: new NgfFormControl<string>(null),
        dialogService: null
    };

    /**
     * Register the template extension with Quill
     * add any internal logic that needs to be done at registration time
     * and does not require an instance of the extension
     */
    static register(): void {
        this.registerToolbarIcon();
    }

    // note: options are passed in via the Quill editor config when the editor is instantiated
    // e.g. for this module, we must have a `template` member in the `modules` config object
    constructor(quill: Quill, options: Partial<NgfRichTextTemplateExtensionOptions>) {
        super(quill, options);

        this.templates = options.templates;
        this.dialogService = options.dialogService;

        this.setupTemplateHandler();
    }

    /**
     * Set up the template toolbar handler
     */
    private setupTemplateHandler(): void {
        const toolbar = this.quill.getModule('toolbar') as Toolbar;
        if (toolbar) {
            toolbar.addHandler('template', this.handleTemplateSelection.bind(this));
        }
    }

    /**
     * Register the template icon in Quill's icon registry
     */
    static registerToolbarIcon(): void {
        const icons = Quill.import('ui/icons');
        icons['template'] = '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14,2 14,8 20,8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10,9 9,9 8,9"></polyline></svg>';
    }

    /**
     * Handle template selection from toolbar
     */
    private handleTemplateSelection(): void {
        const templateList = typeof this.templates === 'function' ? this.templates() : this.templates;

        if (!templateList || templateList.length === 0) {
            console.warn('No templates available for insertion');
            return;
        }

        if (!this.dialogService) {
            throw new Error('[NgfRichTextTemplateExtension] DialogService is required in editor config');
        }

        this.dialogService.open(TextEditorTemplateSelectDialogComponent, {
            data: {
                templates: templateList
            }
        }).afterClosed().pipe(
            tap((result: TextEditorSelectionEvent | undefined) => {
                if (!!result) {
                    this.insertTemplate(result.template, result.replaceSelection);
                }
            })
        ).subscribe();
    }

    /**
     * Insert the selected template into the editor
     */
    private insertTemplate(template: TextEditorTemplate, shouldReplace: boolean): void {

        if (shouldReplace) {
            this.quill.setContents(this.quill.clipboard.convert({html: template.content}), 'user');
        } else {
            const previousContent = this.quill.getContents();
            this.quill.setContents(this.quill.clipboard.convert({html:template.content}), 'user');
            
            // append the previous content after (above) the template content
            this.quill.updateContents(previousContent, 'user');
        }
    }
}